import React, { createContext, FC, useContext } from 'react';
import { useFirebase } from './FirebaseProvider';
import { getDownloadURL, ref } from 'firebase/storage';

const IMAGE_STORE_PATH = 'v0/image-store'

/**
 * The context type for the Firebase image provider.
 */
export type FirebaseImageContextType = {
    /**
     * Loads the image with the given ID as a blob. 
     * @param imageId The ID of the image to load. Do not include the file extension.
     * @returns The blob corresponding to the image contents.
     */
    getImageContent: (imageId: string) => Promise<Blob>;

    /**
     * Gets the URL for the image with the given ID.
     * @param imageId The ID of the image to get the URL for.
     * @returns The URL for the image.
     */
    getImageUrl: (imageId: string) => Promise<string>;

    /**
     * Gets the image with the given ID as a bitmap.
     * @param imageId The ID of the image to get as a bitmap.
     * @returns The image as a bitmap.
     */
    getImageAsBitmap: (imageId: string) => Promise<ImageBitmap>;
}

export const FirebaseImageContext = createContext<FirebaseImageContextType | undefined>(undefined);

export const FirebaseImageProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { storage } = useFirebase();

    const imageStoreRef = ref(storage, IMAGE_STORE_PATH);

    const getImageUrl = async (imageId: string) => {
        const imageRef = ref(imageStoreRef, `${imageId}.png`);

        return await getDownloadURL(imageRef);
    }

    const getImageContent = async (imageId: string) => {
        const url = await getImageUrl(imageId);
        const response = await fetch(url);

        return await response.blob();
    }

    const getImageAsBitmap = async (imageId: string) => {
        const blob = await getImageContent(imageId);

        return await createImageBitmap(blob);
    }

    return (
        <FirebaseImageContext.Provider value={{
            getImageContent,
            getImageUrl,
            getImageAsBitmap
        }}>
            {children}
        </FirebaseImageContext.Provider>
    )
}

export const useFirebaseImage = () => {
    const context = useContext(FirebaseImageContext);

    if (!context) {
        throw new Error("useFirebaseImageContext must be used within a FirebaseImageContextProvider");
    }

    return context;
}
